import { useEffect } from 'react';
import {
  formatSystemLabel,
  getAccountingEventUrl,
  getAccountingLinkLabel,
  getCrmEventUrl,
  getCrmLinkLabel,
} from '../utils/eventExternalLinks';
import { formatEasternDate } from '../utils/eventDates';

function formatOccurredAt(ms) {
  if (ms == null || Number.isNaN(Number(ms))) return null;
  return formatEasternDate(new Date(Number(ms)).toISOString());
}

function isSuccessCode(code) {
  return code >= 200 && code < 300;
}

function formatAmount(amount) {
  return parseFloat(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDetailLabel(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function hasTargetDetails(event) {
  return Boolean(
    event.target_customer
    || event.target_type
    || event.target_doc_num
    || event.target_doc_id
    || event.target_status
    || event.total_amount != null
    || (event.target_details && typeof event.target_details === 'object'
      && Object.values(event.target_details).some((value) => value != null && value !== '')),
  );
}

export default function EventDrawer({ event, company, onClose }) {
  const open = event !== null;
  const success = event ? isSuccessCode(event.status_code) : false;
  const crmUrl = event ? getCrmEventUrl(event, company) : null;
  const accountingUrl = event ? getAccountingEventUrl(event, company) : null;
  const showExternalLinks = Boolean(crmUrl || accountingUrl);
  const lineItems = event?.stream_data?.line_items || [];
  const hasStreamDeal = Boolean(event?.stream_data?.deal_data);
  const hasIntegration = Boolean(event?.source_system || event?.target_system);
  const targetDetailsEntries = event?.target_details && typeof event.target_details === 'object'
    ? Object.entries(event.target_details).filter(([, value]) => value != null && value !== '')
    : [];
  const occurredAtLabel = event?.event_data?.occurredAt != null
    ? formatOccurredAt(event.event_data.occurredAt)
    : null;

  useEffect(() => {
    if (!open) return undefined;
    document.documentElement.classList.add('drawer-open');
    return () => document.documentElement.classList.remove('drawer-open');
  }, [open]);

  return (
    <>
      <div
        className={`drawer-overlay${open ? ' open' : ''}`}
        onClick={onClose}
      />
      <aside className={`drawer${open ? ' open' : ''}`}>
        {event && (
          <>
            <div className="drawer-header">
              <div className="drawer-header-left">
                <span className="drawer-event-id">#{event.id}</span>
                <span className={`status-badge ${success ? 'status-badge--success' : 'status-badge--error'}`}>
                  {success ? '✓' : '✕'} {event.status_code}
                </span>
              </div>
              <button className="drawer-close" onClick={onClose} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="drawer-title-block">
              <h2 className="drawer-event-type">{event.event_type}</h2>
              <p className="drawer-event-date">{formatEasternDate(event.create_date)}</p>
            </div>

            <div className="drawer-body">
              <div className={`drawer-msg ${success ? 'event-detail-msg--success' : 'event-detail-msg--error'}`}>
                {event.msg}
              </div>

              {showExternalLinks && (
                <div className="drawer-external-links">
                  {crmUrl && (
                    <a
                      href={crmUrl}
                      className="btn btn-secondary drawer-external-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {getCrmLinkLabel(event)}
                    </a>
                  )}
                  {accountingUrl && (
                    <a
                      href={accountingUrl}
                      className="btn btn-secondary drawer-external-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {getAccountingLinkLabel(event)}
                    </a>
                  )}
                </div>
              )}

              {hasIntegration && (
                <div className="drawer-section">
                  <div className="drawer-section-title">Integration</div>
                  <div className="drawer-kv-grid">
                    {event.source_system && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Source</span>
                        <span className="drawer-kv-value">{formatSystemLabel(event.source_system)}</span>
                      </div>
                    )}
                    {event.target_system && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Target</span>
                        <span className="drawer-kv-value">{formatSystemLabel(event.target_system)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {event.resolution && (
                <div className="drawer-section">
                  <div className="drawer-section-title">Resolution</div>
                  <div className="drawer-msg drawer-msg--section event-detail-msg--success">
                    {event.resolution}
                  </div>
                </div>
              )}

              {hasTargetDetails(event) && (
                <div className="drawer-section">
                  <div className="drawer-section-title">Target</div>
                  <div className="drawer-kv-grid">
                    {event.target_customer && (
                      <div className="drawer-kv drawer-kv--full">
                        <span className="drawer-kv-label">Customer</span>
                        <span className="drawer-kv-value">{event.target_customer}</span>
                      </div>
                    )}
                    {event.target_type && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Type</span>
                        <span className="drawer-kv-value">{event.target_type}</span>
                      </div>
                    )}
                    {event.target_doc_num && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Document #</span>
                        <span className="drawer-kv-value drawer-kv-mono">{event.target_doc_num}</span>
                      </div>
                    )}
                    {event.target_doc_id && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Target ID</span>
                        <span className="drawer-kv-value drawer-kv-mono">{event.target_doc_id}</span>
                      </div>
                    )}
                    {event.target_status && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Status</span>
                        <span className="drawer-kv-value">{event.target_status}</span>
                      </div>
                    )}
                    {event.total_amount != null && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Amount</span>
                        <span className="drawer-kv-value">${formatAmount(event.total_amount)}</span>
                      </div>
                    )}
                    {targetDetailsEntries.map(([key, value]) => (
                      <div key={key} className="drawer-kv">
                        <span className="drawer-kv-label">{formatDetailLabel(key)}</span>
                        <span className="drawer-kv-value">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {hasStreamDeal && (
                <div className="drawer-section">
                  <div className="drawer-section-title">Deal</div>
                  <div className="drawer-kv-grid">
                    <div className="drawer-kv drawer-kv--full">
                      <span className="drawer-kv-label">Deal Name</span>
                      <span className="drawer-kv-value">{event.stream_data.deal_data.dealname}</span>
                    </div>
                    {event.stream_data.deal_data.terms && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Terms</span>
                        <span className="drawer-kv-value">{event.stream_data.deal_data.terms}</span>
                      </div>
                    )}
                    {event.stream_data.deal_data.payment_method && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Payment Method</span>
                        <span className="drawer-kv-value">{event.stream_data.deal_data.payment_method}</span>
                      </div>
                    )}
                    {event.stream_data?.inv_data?.hs_number && (
                      <div className="drawer-kv">
                        <span className="drawer-kv-label">Invoice #</span>
                        <span className="drawer-kv-value drawer-kv-mono">{event.stream_data.inv_data.hs_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="drawer-section">
                <div className="drawer-section-title">Trigger</div>
                <div className="drawer-kv-grid">
                  {event.event_data?.propertyName != null && (
                    <div className="drawer-kv drawer-kv--full">
                      <span className="drawer-kv-label">Property</span>
                      <code className="event-code">
                        {event.event_data.propertyName}: {event.event_data.propertyValue ?? '—'}
                      </code>
                    </div>
                  )}
                  {event.event_data?.subscriptionType && (
                    <div className="drawer-kv drawer-kv--full">
                      <span className="drawer-kv-label">Subscription</span>
                      <span className="drawer-kv-value drawer-kv-mono">{event.event_data.subscriptionType}</span>
                    </div>
                  )}
                  {event.event_data?.changeSource != null && (
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Source</span>
                      <span className="drawer-kv-value">{String(event.event_data.changeSource).trim()}</span>
                    </div>
                  )}
                  {occurredAtLabel && (
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Occurred</span>
                      <span className="drawer-kv-value">{occurredAtLabel}</span>
                    </div>
                  )}
                  <div className="drawer-kv">
                    <span className="drawer-kv-label">Object ID</span>
                    <span className="drawer-kv-value drawer-kv-mono">{event.object_id}</span>
                  </div>
                </div>
              </div>

              {lineItems.length > 0 && (
                <div className="drawer-section">
                  <div className="drawer-section-title">Line Items</div>
                  <div className="event-line-items">
                    {lineItems.map((item, i) => (
                      <div key={i} className="event-line-item">
                        <span className="event-line-name">{item.name}</span>
                        <span className="event-line-amount">${formatAmount(item.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="drawer-section">
                <div className="drawer-section-title">Notifications</div>
                <div className="drawer-notify-row">
                  <span className={`event-notify-badge${event.sent_to_user ? ' sent' : ''}`}>
                    {event.sent_to_user ? '✓' : '✕'} Sent to user
                  </span>
                  <span className={`event-notify-badge${event.sent_to_customer ? ' sent' : ''}`}>
                    {event.sent_to_customer ? '✓' : '✕'} Sent to customer
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
